import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CircularProgress } from '@mui/material';

import './styles.scss';

import { AllCommunityModule, ColDef, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import classNames from 'classnames';
import { useThemeMode } from '../../providers/theme-provider';
ModuleRegistry.registerModules([AllCommunityModule]);

const getHeaderClass = (align: 'left' | 'right' | 'center' = 'left') => {
  if (align === 'right') return 'header-flex-end';
  if (align === 'center') return 'header-align-center';
  return '';
};

const getDefaultCellTextAlign = (align: 'left' | 'right' | 'center' = 'left') => {
  switch (align) {
    case 'right':
      return 'end';
    case 'center':
      return 'center';
    case 'left':
    default:
      return 'start';
  }
};

type TableGridColumn = ColDef & {
  align?: 'left' | 'right' | 'center';
};

type TableGridProps = {
  columns: TableGridColumn[];
  rowData: any[];
  onCellClicked?: (event: any) => void;
  className?: string;
  autoHeight?: boolean;
  noWatermark?: boolean;
  isLoading?: boolean;
  suppressHorizontalScroll?: boolean;
  suppressColumnMove?: boolean;
  autoSizeStrategy?: any;
  fitWidth?: boolean;
  disableAutoSize?: boolean;
  // Per-instance density overrides — every other TableGrid consumer keeps
  // the existing 14px/32px defaults untouched unless it opts in.
  fontSize?: string;
  rowHeight?: number;
};

const TableGrid: React.FC<TableGridProps> = ({
  columns,
  rowData,
  onCellClicked,
  className,
  autoHeight,
  noWatermark,
  isLoading,
  suppressHorizontalScroll,
  suppressColumnMove,
  autoSizeStrategy,
  fitWidth,
  disableAutoSize,
  fontSize,
  rowHeight,
}) => {
  const { resolvedMode } = useThemeMode();

  const MIN_COL_WIDTH = 80;

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: MIN_COL_WIDTH,
      wrapText: false,
      cellStyle: { whiteSpace: 'nowrap' },
    }),
    [],
  );

  const normalizedColumns = useMemo<ColDef[]>(
    () =>
      (columns || []).map((col) => ({
        ...col,
        minWidth: Math.max(col.minWidth ?? 0, MIN_COL_WIDTH),
        cellStyle: {
          textAlign: getDefaultCellTextAlign(col.align),
          ...col.cellStyle,
        },
        headerClass: classNames(col.headerClass, getHeaderClass(col.align)),
      })),
    [columns],
  );

  const myTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'transparent',
        foregroundColor: resolvedMode === 'dark' ? '#FAFAFA' : '#092D3A',
        oddRowBackgroundColor: resolvedMode === 'dark' ? '#5B8FDF1A' : '#C2E5FF59',
        fontFamily: 'Inter',
        fontSize: fontSize ?? '14px',
        headerRowBorder: false,
        headerColumnResizeHandleWidth: '0px',
        columnBorder: false,
        rowBorder: false,
        wrapperBorder: false,
        spacing: '8px',
      }),
    [resolvedMode, fontSize],
  );

  const resolvedAutoSizeStrategy = useMemo(
    () =>
      autoSizeStrategy ??
      ({
        type: fitWidth ? 'fitGridWidth' : 'fitCellContents',
      } as const),
    [autoSizeStrategy, fitWidth],
  );

  const classnames = classNames('table-grid-container', className, {
    'show-watermark': !noWatermark,
    'is-loading': isLoading,
    'watermark-dark': resolvedMode === 'dark',
    'watermark-light': resolvedMode === 'light',
  });

  return (
    <div className={classnames}>
      <div className="table-grid-inner">
        <AgGridReact
          columnDefs={normalizedColumns}
          rowData={isLoading ? [] : rowData}
          rowHeight={rowHeight ?? 32}
          defaultColDef={defaultColDef}
          onCellClicked={onCellClicked}
          className="table-grid-default-theme"
          theme={myTheme}
          suppressCellFocus={true}
          suppressHorizontalScroll={suppressHorizontalScroll}
          suppressMovableColumns={suppressColumnMove}
          {...(!disableAutoSize ? { autoSizeStrategy: resolvedAutoSizeStrategy } : {})}
          {...(autoHeight ? { domLayout: 'autoHeight' } : {})}
        />
        {isLoading && (
          <div className="table-grid-loading">
            <CircularProgress size={34} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TableGrid;
